/**
 * ExpenseFilters Component
 * Filter UI for Expense overview
 */

import React, { useMemo, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import Modal from "react-modal";
import Calendar from "../../../helpers/dateComponents/Calendar";
import TruncateFilterFieldView from "../../../components/helpers/truncateFilterFieldView.js";
import { formatUserName, canFilterByTeam } from "../utils/expenseUtils";
import { MODAL_STYLES, EXPENSE_STATUS_LIST } from "../utils/expenseConstants";

const ExpenseFilters = ({
  // Filter state
  dateRange,
  showFilterModal,
  showCalendar,
  showModalCalendar,
  showConfirmationModal,
  
  // Selected filter values
  selectedTeams = [],
  selectedClients = [],
  selectedProjects = [],
  selectedDepts = [],
  selectedExpCategories = [],
  selectedExpCurrenciesFilter = [],
  selectedExpStatusFilter = [],
  
  // Dropdown open states
  isTeamDropdownOpen = false,
  isTeamModalDropdownOpen = false,
  isClientDropdownOpen = false,
  isClientModalDropdownOpen = false,
  isProjectDropdownOpen = false,
  isProjectModalDropdownOpen = false,
  isDeptDropdownOpen = false,
  isDeptModalDropdownOpen = false,
  isExpCategoryDropdownOpen = false,
  isExpCategoryModalDropdownOpen = false,
  isExpCurrencyFilterDropdownOpen = false,
  isExpCurrencyFilterModalDropdownOpen = false,
  isExpStatusFilterDropdownOpen = false,
  
  // Select all states
  selectAllTeam = false,
  selectAllClient = false,
  selectAllProject = false,
  selectAllDept = false,
  selectAllExpCategory = false,
  selectAllExpCurrencyFilter = false,
  selectAllExpStatusFilter = false,
  
  // Search terms
  userDropdownSearchTerm = "",
  clientDropdownSearchTerm = "",
  expProjectSearchTerm = "",
  departmentDropdownSearchTerm = "",
  categoryDropdownSearchTerm = "",
  expCurrencyDropdownSearchTerm = "",
  expStatusDropdownSearchTerm = "",
  
  // Data
  teams = [],
  clients = [],
  projects = [],
  departments = [],
  expenseCategory = [],
  expCurrencyList = [],
  user,
  
  // Handlers
  onDateChange,
  onToggleFilterModal,
  onToggleCalendar,
  onCancelCalendar,
  onToggleModalCalendar,
  onCancelModalCalendar,
  onClearFilters,
  onApplyFilters,
  onCheckAndShowConfirmationModal,
  onCancel,
  onApplyConfirm,
  
  // Filter change handlers
  onCheckboxTeamChange,
  onSelectAllTeamChange,
  onToggleTeamDropdown,
  onToggleTeamModalDropdown,
  onUserDropdownSearchChange,
  
  onCheckboxClientChange,
  onSelectAllClientChange,
  onToggleClientDropdown,
  onToggleClientModalDropdown,
  onClientDropdownSearchChange,
  
  onCheckboxProjectChange,
  onSelectAllProjectChange,
  onToggleProjectDropdown,
  onToggleProjectModalDropdown,
  onProjectSearchChange,
  
  onCheckboxDeptChange,
  onSelectAllDeptChange,
  onToggleDeptDropdown,
  onToggleDeptModalDropdown,
  onDepartmentDropdownSearchChange,
  
  onCheckboxExpCategoryChange,
  onSelectAllExpCategoryChange,
  onToggleExpCategoryDropdown,
  onToggleExpCategoryModalDropdown,
  onCategoryDropdownSearchChange,
  
  onCheckboxExpCurrencyFilterChange,
  onSelectAllExpCurrencyFilterChange,
  onToggleExpCurrencyFilterDropdown,
  onToggleExpCurrencyFilterModalDropdown,
  onExpCurrencyDropdownSearchChange,
  
  onCheckboxExpStatusFilterChange,
  onSelectAllExpStatusFilterChange,
  onToggleExpStatusFilterDropdown,
  onExpStatusDropdownSearchChange,
  
  // Refs
  calendarRef,
  calendarModalRef,
  calendarModalPortalRef,
  teamDropdownRef,
  teamModalDropdownRef,
  clientDropdownRef,
  clientModalDropdownRef,
  projectDropdownRef,
  projectModalDropdownRef,
  deptDropdownRef,
  deptModalDropdownRef,
  expCategoryDropdownRef,
  expCategoryModalDropdownRef,
  expCurrencyFilterDropdownRef,
  expCurrencyFilterModalDropdownRef,
  expStatusFilterDropdownRef,
  
  // Initial values
  startOfYear,
  endOfToday,
}) => {
  const [isMobileFilter, setIsMobileFilter] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 767
  );

  useEffect(() => {
    const onResize = () => setIsMobileFilter(window.innerWidth <= 767);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const filterModalStyle = useMemo(() => {
    if (!isMobileFilter) return MODAL_STYLES.FILTER_MODAL;
    return {
      content: {
        position: "fixed",
        top: "auto",
        left: 0,
        right: 0,
        bottom: 0,
        inset: "auto 0 0 0",
        width: "100%",
        maxWidth: "100%",
        height: "auto",
        maxHeight: "min(90vh, 90dvh)",
        margin: 0,
        transform: "none",
        borderRadius: "24px 24px 0 0",
        border: "none",
        padding: "16px 16px 0 16px",
        backgroundColor: "#fff",
        boxShadow: "0 -8px 30px rgba(0, 0, 0, 0.16)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
      },
      overlay: {
        backgroundColor: "rgba(0, 0, 0, 0.45)",
        zIndex: 99999,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "stretch",
      },
    };
  }, [isMobileFilter]);

  // Memoize initialRange to prevent Calendar from resetting on every render
  const memoizedInitialRange = useMemo(() => {
    const start = dateRange ? new Date(dateRange[0]) : null;
    const end = dateRange ? new Date(dateRange[1]) : null;
    return {
      startDate: start,
      endDate: end,
    };
  }, [dateRange]);

  // Memoized filtered lists
  const filteredDropdownUsers = useMemo(() => {
    return teams.filter((user) => {
      const fullName = formatUserName(user);
      return fullName.toLowerCase().includes(userDropdownSearchTerm.toLowerCase());
    });
  }, [teams, userDropdownSearchTerm]);

  const filteredDropdownClients = useMemo(() => {
    return clients.filter((client) => {
      const clientName = client.client_name || client.legal_entity_name;
      return clientName?.toLowerCase().includes(clientDropdownSearchTerm.toLowerCase());
    });
  }, [clients, clientDropdownSearchTerm]);

  const filteredDropdownProjects = useMemo(() => {
    return projects.filter((project) => {
      return project?.project_name?.toLowerCase().includes(expProjectSearchTerm.toLowerCase());
    });
  }, [projects, expProjectSearchTerm]);

  const filteredDropdownDepartments = useMemo(() => {
    return departments.filter((dept) => {
      return dept?.user_department?.toLowerCase().includes(departmentDropdownSearchTerm.toLowerCase());
    });
  }, [departments, departmentDropdownSearchTerm]);

  const filteredDropdownExpenseCategory = useMemo(() => {
    return expenseCategory.filter((cat) => {
      return cat?.exp_category_name?.toLowerCase().includes(categoryDropdownSearchTerm.toLowerCase());
    });
  }, [expenseCategory, categoryDropdownSearchTerm]);

  const filteredDropdownExpCurrencyList = useMemo(() => {
    return expCurrencyList.filter((expCurrency) =>
      `${expCurrency.currencyCode} (${expCurrency.currencyName})`
        .toLowerCase()
        .includes(expCurrencyDropdownSearchTerm.toLowerCase())
    );
  }, [expCurrencyList, expCurrencyDropdownSearchTerm]);

  const filteredExpStatusList = useMemo(() => {
    return EXPENSE_STATUS_LIST.filter((status) =>
      status.label.toLowerCase().includes(expStatusDropdownSearchTerm.toLowerCase())
    );
  }, [expStatusDropdownSearchTerm]);

  const canShowTeamFilter = canFilterByTeam(user?.userType);

  return (
    <>
      {/* Desktop Filters */}
      <div className={`filter-sec-overview active`}>
        <div className="invoice-filter-inner">
          <div className="invoice-filter-inner-section no-flter-all">
            {/* Team Filter */}
            {canShowTeamFilter && (
              <div className="filter-inner invoice-id">
                <label>Team</label>
                <div className="custom-dropdown1" ref={teamDropdownRef}>
                  <div className="drop-down-inner">
                    <div className="selected-options1" onClick={onToggleTeamDropdown}>
                      {selectAllTeam ? (
                        <div className="selected-option1">All</div>
                      ) : selectedTeams.length === 0 ? (
                        <div className="placeholder">Select</div>
                      ) : (
                        <div className="selected-option1" key={selectedTeams[0]}>
                          {selectedTeams.length === teams.length ? (
                            'All'
                          ) : (
                            <>
                              <TruncateFilterFieldView
                                textContent={formatUserName(
                                  teams.find((item) => item._id === selectedTeams[0])
                                )}
                              />
                              {selectedTeams.length > 1 && selectedTeams.length !== teams.length && (
                                <div className="selected-option-count">
                                  {selectedTeams.length - 1}+
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {isTeamDropdownOpen && (
                      <div className="dropdown-list1">
                        <input
                          type="text"
                          placeholder="Search teams"
                          value={userDropdownSearchTerm}
                          onChange={onUserDropdownSearchChange}
                          className="search-bar"
                        />
                        {filteredDropdownUsers.length > 0 && (
                          <>
                            {userDropdownSearchTerm === "" && (
                              <label className="checkbox-label1" key="selectAll">
                                <input
                                  type="checkbox"
                                  checked={selectAllTeam}
                                  onChange={onSelectAllTeamChange}
                                />
                                All Teams
                              </label>
                            )}
                            {filteredDropdownUsers.map((team) => (
                              <label className="checkbox-label1" key={team._id}>
                                <input
                                  type="checkbox"
                                  checked={selectedTeams.includes(team._id)}
                                  onChange={() => onCheckboxTeamChange(team._id)}
                                />
                                {formatUserName(team)}
                              </label>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Client Filter */}
            <div className="filter-inner user">
              <label htmlFor="user">Client</label>
              <div className="custom-dropdown1" ref={clientDropdownRef}>
                <div className="drop-down-inner">
                  <div className="selected-options1" onClick={onToggleClientDropdown}>
                    {selectAllClient ? (
                      <div className="selected-option1">All</div>
                    ) : selectedClients.length === 0 ? (
                      <div className="placeholder">Select</div>
                    ) : (
                      <div className="selected-option1" key={selectedClients[0]}>
                        {selectedClients.length === clients.length ? (
                          'All'
                        ) : (
                          <>
                            <TruncateFilterFieldView
                              textContent={clients.find((item) => item._id === selectedClients[0])?.client_name}
                            />
                            {selectedClients.length > 1 && selectedClients.length !== clients.length && (
                              <div className="selected-option-count">
                                {selectedClients.length - 1}+
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {isClientDropdownOpen && (
                    <div className="dropdown-list1">
                      <input
                        type="text"
                        placeholder="Search clients"
                        value={clientDropdownSearchTerm}
                        onChange={onClientDropdownSearchChange}
                        className="search-bar"
                      />
                      {filteredDropdownClients.length > 0 && (
                        <>
                          {clientDropdownSearchTerm === "" && (
                            <label className="checkbox-label1" key="selectAll">
                              <input
                                type="checkbox"
                                checked={selectAllClient}
                                onChange={onSelectAllClientChange}
                              />
                              All Clients
                            </label>
                          )}
                          {filteredDropdownClients
                            .slice()
                            .sort((a, b) =>
                              (a.client_name || a.legal_entity_name || "")
                                ?.toLowerCase()
                                .localeCompare((b.client_name || b.legal_entity_name || "")?.toLowerCase())
                            )
                            .map((client) => (
                              <label className="checkbox-label1" key={client._id}>
                                <input
                                  type="checkbox"
                                  checked={selectedClients.includes(client._id)}
                                  onChange={() => onCheckboxClientChange(client._id)}
                                />
                                {client.client_name || client.legal_entity_name}
                              </label>
                            ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Department Filter */}
            <div className="filter-inner payment-status">
              <label htmlFor="paymentStatus">Department</label>
              <div className="custom-dropdown1" ref={deptDropdownRef}>
                <div className="drop-down-inner">
                  <div className="selected-options1" onClick={onToggleDeptDropdown}>
                    {selectAllDept ? (
                      <div className="selected-option1">All</div>
                    ) : selectedDepts.length === 0 ? (
                      <div className="placeholder">Select</div>
                    ) : (
                      <div className="selected-option1" key={selectedDepts[0]}>
                        {selectedDepts.length === departments.length ? (
                          'All'
                        ) : (
                          <>
                            <TruncateFilterFieldView
                              textContent={departments.find((item) => item._id === selectedDepts[0])?.user_department}
                            />
                            {selectedDepts.length > 1 && selectedDepts.length !== departments.length && (
                              <div className="selected-option-count">
                                {selectedDepts.length - 1}+
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {isDeptDropdownOpen && (
                    <div className="dropdown-list1">
                      <input
                        type="text"
                        placeholder="Search departments"
                        value={departmentDropdownSearchTerm}
                        onChange={onDepartmentDropdownSearchChange}
                        className="search-bar"
                      />
                      {filteredDropdownDepartments.length > 0 && (
                        <>
                          {departmentDropdownSearchTerm === "" && (
                            <label className="checkbox-label1" key="selectAll">
                              <input
                                type="checkbox"
                                checked={selectAllDept}
                                onChange={onSelectAllDeptChange}
                              />
                              All Departments
                            </label>
                          )}
                          {filteredDropdownDepartments
                            .slice()
                            .sort((a, b) =>
                              a.user_department?.toLowerCase().localeCompare(b.user_department?.toLowerCase())
                            )
                            .map((dept) => (
                              <label className="checkbox-label1" key={dept._id}>
                                <input
                                  type="checkbox"
                                  checked={selectedDepts.includes(dept._id)}
                                  onChange={() => onCheckboxDeptChange(dept._id)}
                                />
                                {dept.user_department}
                              </label>
                            ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Expense Category Filter */}
            <div className="filter-inner payment-status">
              <label htmlFor="paymentStatus">Expense Category</label>
              <div className="custom-dropdown1" ref={expCategoryDropdownRef}>
                <div className="drop-down-inner">
                  <div className="selected-options1" onClick={onToggleExpCategoryDropdown}>
                    {selectAllExpCategory ? (
                      <div className="selected-option1">All</div>
                    ) : selectedExpCategories.length === 0 ? (
                      <div className="placeholder">Select</div>
                    ) : (
                      <div className="selected-option1" key={selectedExpCategories[0]}>
                        {selectedExpCategories.length === expenseCategory.length ? (
                          'All'
                        ) : (
                          <>
                            <TruncateFilterFieldView
                              textContent={expenseCategory.find((item) => item._id === selectedExpCategories[0])?.exp_category_name}
                            />
                            {selectedExpCategories.length > 1 && selectedExpCategories.length !== expenseCategory.length && (
                              <div className="selected-option-count">
                                {selectedExpCategories.length - 1}+
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {isExpCategoryDropdownOpen && (
                    <div className="dropdown-list1">
                      <input
                        type="text"
                        placeholder="Search categories"
                        value={categoryDropdownSearchTerm}
                        onChange={onCategoryDropdownSearchChange}
                        className="search-bar"
                      />
                      {filteredDropdownExpenseCategory.length > 0 && (
                        <>
                          {categoryDropdownSearchTerm === "" && (
                            <label className="checkbox-label1" key="selectAll">
                              <input
                                type="checkbox"
                                checked={selectAllExpCategory}
                                onChange={onSelectAllExpCategoryChange}
                              />
                              All Expense Category
                            </label>
                          )}
                          {filteredDropdownExpenseCategory
                            .slice()
                            .sort((a, b) =>
                              a.exp_category_name?.toLowerCase().localeCompare(b.exp_category_name?.toLowerCase())
                            )
                            .map((expCat) => (
                              <label className="checkbox-label1" key={expCat._id}>
                                <input
                                  type="checkbox"
                                  checked={selectedExpCategories.includes(expCat._id)}
                                  onChange={() => onCheckboxExpCategoryChange(expCat._id)}
                                />
                                {expCat.exp_category_name}
                              </label>
                            ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Expense Currency Filter */}
            <div className="filter-inner payment-status">
              <label htmlFor="paymentStatus">Expense Currency</label>
              <div className="custom-dropdown1" ref={expCurrencyFilterDropdownRef}>
                <div className="drop-down-inner">
                  <div className="selected-options1" onClick={onToggleExpCurrencyFilterDropdown}>
                    {selectAllExpCurrencyFilter ? (
                      <div className="selected-option1">All</div>
                    ) : selectedExpCurrenciesFilter.length === 0 ? (
                      <div className="placeholder">Select</div>
                    ) : (
                      <div className="selected-option1" key={selectedExpCurrenciesFilter[0]}>
                        {selectedExpCurrenciesFilter.length === expCurrencyList.length ? (
                          'All'
                        ) : (
                          <>
                            <TruncateFilterFieldView
                              textContent={expCurrencyList.find((item) => item.currencyCode === selectedExpCurrenciesFilter[0])?.currencyName}
                            />
                            {selectedExpCurrenciesFilter.length > 1 && selectedExpCurrenciesFilter.length !== expCurrencyList.length && (
                              <div className="selected-option-count">
                                {selectedExpCurrenciesFilter.length - 1}+
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {isExpCurrencyFilterDropdownOpen && (
                    <div className="dropdown-list1">
                      <input
                        type="text"
                        placeholder="Search currency"
                        value={expCurrencyDropdownSearchTerm}
                        onChange={onExpCurrencyDropdownSearchChange}
                        className="search-bar"
                      />
                      {filteredDropdownExpCurrencyList.length > 0 && (
                        <>
                          {expCurrencyDropdownSearchTerm === "" && (
                            <label className="checkbox-label1" key="selectAll">
                              <input
                                type="checkbox"
                                checked={selectAllExpCurrencyFilter}
                                onChange={onSelectAllExpCurrencyFilterChange}
                              />
                              All
                            </label>
                          )}
                          {filteredDropdownExpCurrencyList
                            .slice()
                            .sort((a, b) =>
                              a.currencyName?.toLowerCase().localeCompare(b.currencyName?.toLowerCase())
                            )
                            .map((expCur) => (
                              <label className="checkbox-label1" key={expCur.currencyCode}>
                                <input
                                  type="checkbox"
                                  checked={selectedExpCurrenciesFilter.includes(expCur.currencyCode)}
                                  onChange={() => onCheckboxExpCurrencyFilterChange(expCur.currencyCode)}
                                />
                                {expCur.currencyCode && `${expCur.currencyCode} (${expCur.currencyName})`}
                              </label>
                            ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Expense Status Filter */}
            <div className="filter-inner payment-status">
              <label htmlFor="expenseStatus">Expense Status</label>
              <div className="custom-dropdown1" ref={expStatusFilterDropdownRef}>
                <div className="drop-down-inner">
                  <div className="selected-options1" onClick={onToggleExpStatusFilterDropdown}>
                    {selectAllExpStatusFilter ? (
                      <div className="selected-option1">All</div>
                    ) : selectedExpStatusFilter.length === 0 ? (
                      <div className="placeholder">Select</div>
                    ) : (
                      <div className="selected-option1" key={selectedExpStatusFilter[0]}>
                        {selectedExpStatusFilter.length === EXPENSE_STATUS_LIST.length ? (
                          'All'
                        ) : (
                          <>
                            <TruncateFilterFieldView
                              textContent={EXPENSE_STATUS_LIST.find((item) => item.value === selectedExpStatusFilter[0])?.label}
                            />
                            {selectedExpStatusFilter.length > 1 && selectedExpStatusFilter.length !== EXPENSE_STATUS_LIST.length && (
                              <div className="selected-option-count">
                                {selectedExpStatusFilter.length - 1}+
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  {isExpStatusFilterDropdownOpen && (
                    <div className="dropdown-list1">
                      <input
                        type="text"
                        placeholder="Search status"
                        value={expStatusDropdownSearchTerm}
                        onChange={onExpStatusDropdownSearchChange}
                        className="search-bar"
                      />
                      {filteredExpStatusList.length > 0 && (
                        <>
                          {expStatusDropdownSearchTerm === "" && (
                            <label className="checkbox-label1" key="selectAllStatus">
                              <input
                                type="checkbox"
                                checked={selectAllExpStatusFilter}
                                onChange={onSelectAllExpStatusFilterChange}
                              />
                              All
                            </label>
                          )}
                          {filteredExpStatusList
                            .slice()
                            .sort((a, b) => a.label?.toLowerCase().localeCompare(b.label?.toLowerCase()))
                            .map((status) => (
                              <label className="checkbox-label1" key={status.value}>
                                <input
                                  type="checkbox"
                                  checked={selectedExpStatusFilter.includes(status.value)}
                                  onChange={() => onCheckboxExpStatusFilterChange(status.value)}
                                />
                                {status.label}
                              </label>
                            ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Date Range Filter */}
            <div className="filter-inner due-date dd">
              <label htmlFor="dueDate" className="calendar-icon">
                Date Range
              </label>
              <div className="date-picker-outer date-picker-outer-all" ref={calendarRef} style={{ position: 'relative' }}>
                <div className="custom-picker-icon custom-picker-icon-all">
                  <svg
                    width="1em"
                    height="1em"
                    viewBox="0 0 14 14"
                    fill="currentColor"
                    aria-hidden="true"
                    focusable="false"
                    className="rs-picker-toggle-caret rs-icon"
                    aria-label="calendar"
                    data-category="legacy"
                  >
                    <path d="M1 4v8.5a.5.5 0 00.5.5H8c0-.128.049-.256.146-.354.555-.555.854-1.6.854-3.146a.5.5 0 01.621-.485l.119.03A2.623 2.623 0 0012.999 6.5V4h-12zm3-2h6V0h1v2h1.5A1.5 1.5 0 0114 3.5v3a3.623 3.623 0 01-4.015 3.603c-.064 1.245-.335 2.212-.831 2.898H12.5a.5.5 0 00.5-.5v-2a.5.5 0 011 0v2a1.5 1.5 0 01-1.5 1.5h-11a1.5 1.5 0 01-1.5-1.5v-9a1.5 1.5 0 011.5-1.5H3v-2h1v2zm5 4V5h1v1h2v1h-2v1H9V7H7v2h1v1H7v2H6v-2H4v2H3v-2H1V9h2V7H1V6h2V5h1v1h2V5h1v1h2zM6 9V7H4v2h2z"></path>
                  </svg>
                </div>
                <div className="custoom-icon-calender custoom-icon-calender-all">
                  <div className="custoom-icon-calender custoom-icon-calender-all custom-date-range">
                    <input
                      type="text"
                      value={
                        dateRange
                          ? `${new Date(dateRange[0]).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })} to ${new Date(dateRange[1]).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}`
                          : ''
                      }
                      id="invoiceId"
                      name="invoiceId"
                      placeholder="All Time"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleCalendar();
                      }}
                      autoComplete="off"
                      readOnly
                    />
                  </div>
                    {showCalendar && (
                    <div className="date-range-new right-align" style={{ position: 'absolute', zIndex: 1000 }}>
                      <Calendar
                        onCancel={onCancelCalendar}
                        onChange={(range) => {
                          if (range && range.startDate && range.endDate) {
                            // Create new Date objects from the range dates
                            const adjustedStartDate = range.startDate ? new Date(range.startDate) : null;
                            const adjustedEndDate = range.endDate ? new Date(range.endDate) : null;
                            onDateChange({
                              startDate: adjustedStartDate,
                              endDate: adjustedEndDate,
                            });
                          }
                        }}
                        initialRange={memoizedInitialRange}
                      />
                  </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Filter Modal Button */}
          <div className="filter-invoice-right apply-fliter-right">
            <button onClick={onToggleFilterModal}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="17"
                viewBox="0 0 13 17"
                fill="none"
              >
                <path
                  d="M11.7363 8.81108C11.4276 9.81451 10.4925 10.5444 9.38889 10.5444C8.28471 10.5444 7.35016 9.8141 7.04147 8.81108L0.722221 8.81108C0.323349 8.81108 -1.07845e-06 8.48773 -1.06101e-06 8.08886C-1.04357e-06 7.68998 0.323349 7.36663 0.722221 7.36663L7.04144 7.36663C7.35022 6.3632 8.28529 5.6333 9.38889 5.6333C10.4931 5.6333 11.4276 6.36362 11.7363 7.36663L12.2778 7.36663C12.6767 7.36663 13 7.68998 13 8.08886C13 8.48773 12.6766 8.81108 12.2778 8.81108L11.7363 8.81108ZM9.38889 7.07775C8.83162 7.07775 8.37778 7.53123 8.37778 8.08886C8.37778 8.64613 8.83127 9.09997 9.38889 9.09997C9.94616 9.09997 10.4 8.64648 10.4 8.08886C10.4 7.53158 9.94651 7.07775 9.38889 7.07775Z"
                  fill="#6479F8"
                />
                <path
                  d="M5.38078 3.17778C5.072 4.18121 4.13693 4.91111 3.03333 4.91111C1.92882 4.91111 0.994035 4.18035 0.68563 3.17687C0.303759 3.15781 -1.07791e-06 2.84216 -1.06101e-06 2.45556C-1.04411e-06 2.06896 0.303748 1.75331 0.685606 1.73425C0.994098 0.730343 1.9294 -4.83911e-07 3.03333 -4.35657e-07C4.13752 -3.87392e-07 5.07206 0.730315 5.38076 1.73333L12.2778 1.73333C12.6767 1.73333 13 2.05668 13 2.45556C13 2.85443 12.6766 3.17778 12.2778 3.17778L5.38078 3.17778ZM3.03333 1.44444C2.47606 1.44444 2.02222 1.89793 2.02222 2.45556C2.02222 3.01283 2.47571 3.46667 3.03333 3.46667C3.5906 3.46667 4.04444 3.01318 4.04444 2.45556C4.04444 1.89828 3.59096 1.44444 3.03333 1.44444Z"
                  fill="#6479F8"
                />
                <path
                  d="M7.403 14.4444C7.09422 15.4478 6.15916 16.1777 5.05556 16.1777C3.95137 16.1777 3.01682 15.4474 2.70813 14.4444L0.722221 14.4444C0.323349 14.4444 -1.07845e-06 14.121 -1.06101e-06 13.7222C-1.04357e-06 13.3233 0.323349 12.9999 0.722221 12.9999L2.70811 12.9999C3.01689 11.9965 3.95195 11.2666 5.05556 11.2666C6.15974 11.2666 7.09429 11.9969 7.40298 12.9999L12.2778 12.9999C12.6767 12.9999 13 13.3233 13 13.7222C13 14.121 12.6766 14.4444 12.2778 14.4444L7.403 14.4444ZM5.05556 12.711C4.49828 12.711 4.04444 13.1645 4.04444 13.7222C4.04444 14.2794 4.49793 14.7333 5.05556 14.7333C5.61283 14.7333 6.06667 14.2798 6.06667 13.7222C6.06667 13.1649 5.61318 12.711 5.05556 12.711Z"
                  fill="#6479F8"
                />
              </svg>
              All Filters
            </button>
          </div>

          {/* Filter Modal */}
          <Modal
            isOpen={showFilterModal}
            onRequestClose={onCheckAndShowConfirmationModal}
            contentLabel="Filter Modal"
            className="new-filter-modal expense-filter-modal"
            overlayClassName={isMobileFilter ? "expense-filter-overlay" : undefined}
            style={filterModalStyle}
          >
            <div className="new-nodal-invoice-outer-sec">
              <div className="new-modal-top-sec">
                <div className="new-modal-heading">All filters</div>
                <a className="close-image-clr1" onClick={onCheckAndShowConfirmationModal}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 13L13 1" stroke="black" />
                    <path d="M1 0.999999L13 13" stroke="black" />
                  </svg>
                </a>
              </div>
              <div className="invoice-filter-inner-section expense-filter-modal-body">
                {/* Modal filters - similar structure to desktop filters but using modal refs */}
                {/* Team Filter in Modal */}
                {canShowTeamFilter && (
                  <div className="filter-inner invoice-id">
                    <label>Team</label>
                    <div className="custom-dropdown1" ref={teamModalDropdownRef}>
                      <div className="drop-down-inner">
                        <div className="selected-options1" onClick={onToggleTeamModalDropdown}>
                          {selectAllTeam ? (
                            <div className="selected-option1">All</div>
                          ) : selectedTeams.length === 0 ? (
                            <div className="placeholder">Select</div>
                          ) : (
                            <div className="selected-option1" key={selectedTeams[0]}>
                              {selectedTeams.length === teams.length ? (
                                'All'
                              ) : (
                                <>
                                  <TruncateFilterFieldView
                                    textContent={formatUserName(
                                      teams.find((item) => item._id === selectedTeams[0])
                                    )}
                                  />
                                  {selectedTeams.length > 1 && selectedTeams.length !== teams.length && (
                                    <div className="selected-option-count">
                                      {selectedTeams.length - 1}+
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        {isTeamModalDropdownOpen && (
                          <div className="dropdown-list1">
                            <input
                              type="text"
                              placeholder="Search teams"
                              value={userDropdownSearchTerm}
                              onChange={onUserDropdownSearchChange}
                              className="search-bar"
                            />
                            {filteredDropdownUsers.length > 0 && (
                              <>
                                {userDropdownSearchTerm === "" && (
                                  <label className="checkbox-label1" key="selectAll">
                                    <input
                                      type="checkbox"
                                      checked={selectAllTeam}
                                      onChange={onSelectAllTeamChange}
                                    />
                                    All Teams
                                  </label>
                                )}
                                {filteredDropdownUsers.map((team) => (
                                  <label className="checkbox-label1" key={team._id}>
                                    <input
                                      type="checkbox"
                                      checked={selectedTeams.includes(team._id)}
                                      onChange={() => onCheckboxTeamChange(team._id)}
                                    />
                                    {formatUserName(team)}
                                  </label>
                                ))}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Client Filter in Modal */}
                <div className="filter-inner user">
                  <label htmlFor="user">Client</label>
                  <div className="custom-dropdown1" ref={clientModalDropdownRef}>
                    <div className="drop-down-inner">
                      <div className="selected-options1" onClick={onToggleClientModalDropdown}>
                        {selectAllClient ? (
                          <div className="selected-option1">All</div>
                        ) : selectedClients.length === 0 ? (
                          <div className="placeholder">Select</div>
                        ) : (
                          <div className="selected-option1" key={selectedClients[0]}>
                            {selectedClients.length === clients.length ? (
                              'All'
                            ) : (
                              <>
                                <TruncateFilterFieldView
                                  textContent={clients.find((item) => item._id === selectedClients[0])?.client_name}
                                />
                                {selectedClients.length > 1 && selectedClients.length !== clients.length && (
                                  <div className="selected-option-count">
                                    {selectedClients.length - 1}+
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {isClientModalDropdownOpen && (
                        <div className="dropdown-list1">
                          <input
                            type="text"
                            placeholder="Search clients"
                            value={clientDropdownSearchTerm}
                            onChange={onClientDropdownSearchChange}
                            className="search-bar"
                          />
                          {filteredDropdownClients.length > 0 && (
                            <>
                              {clientDropdownSearchTerm === "" && (
                                <label className="checkbox-label1" key="selectAll">
                                  <input
                                    type="checkbox"
                                    checked={selectAllClient}
                                    onChange={onSelectAllClientChange}
                                  />
                                  All Clients
                                </label>
                              )}
                              {filteredDropdownClients
                                .slice()
                                .sort((a, b) =>
                                  (a.client_name || a.legal_entity_name || "")
                                    ?.toLowerCase()
                                    .localeCompare((b.client_name || b.legal_entity_name || "")?.toLowerCase())
                                )
                                .map((client) => (
                                  <label className="checkbox-label1" key={client._id}>
                                    <input
                                      type="checkbox"
                                      checked={selectedClients.includes(client._id)}
                                      onChange={() => onCheckboxClientChange(client._id)}
                                    />
                                    {client.client_name || client.legal_entity_name}
                                  </label>
                                ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Project Filter in Modal */}
                <div className="filter-inner payment-status">
                  <label htmlFor="paymentStatus">Project</label>
                  <div className="custom-dropdown1" ref={projectModalDropdownRef}>
                    <div className="drop-down-inner">
                      <div className="selected-options1" onClick={onToggleProjectModalDropdown}>
                        {selectedProjects.length === 0 ? (
                          <div className="placeholder">{selectAllExpStatusFilter ? "All" : "Select"}</div>
                        ) : (
                          <div className="selected-option1" key={selectedProjects[0]}>
                            {selectedProjects.length === projects.length ? (
                              'All'
                            ) : (
                              <>
                                <TruncateFilterFieldView
                                  textContent={projects.find((item) => item._id === selectedProjects[0])?.project_name}
                                />
                                {selectedProjects.length > 1 && selectedProjects.length !== projects.length && (
                                  <div className="selected-option-count">
                                    {selectedProjects.length - 1}+
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {isProjectModalDropdownOpen && (
                        <div className="dropdown-list1">
                          <input
                            type="text"
                            placeholder="Search projects"
                            value={expProjectSearchTerm}
                            onChange={onProjectSearchChange}
                            className="search-bar"
                          />
                          {filteredDropdownProjects.length > 0 && (
                            <>
                              {expProjectSearchTerm === "" && (
                                <label className="checkbox-label1" key="selectAll">
                                  <input
                                    type="checkbox"
                                    checked={selectAllProject}
                                    onChange={onSelectAllProjectChange}
                                  />
                                  All Projects
                                </label>
                              )}
                              {filteredDropdownProjects
                                .slice()
                                .sort((a, b) =>
                                  a.project_name?.toLowerCase().localeCompare(b.project_name?.toLowerCase())
                                )
                                .map((project) => (
                                  <label className="checkbox-label1" key={project._id}>
                                    <input
                                      type="checkbox"
                                      checked={selectedProjects.includes(project._id)}
                                      onChange={() => onCheckboxProjectChange(project._id)}
                                    />
                                    {project.project_name}
                                  </label>
                                ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Department Filter in Modal */}
                <div className="filter-inner payment-status">
                  <label htmlFor="paymentStatus">Department</label>
                  <div className="custom-dropdown1" ref={deptModalDropdownRef}>
                    <div className="drop-down-inner">
                      <div className="selected-options1" onClick={onToggleDeptModalDropdown}>
                        {selectAllDept ? (
                          <div className="selected-option1">All</div>
                        ) : selectedDepts.length === 0 ? (
                          <div className="placeholder">Select</div>
                        ) : (
                          <div className="selected-option1" key={selectedDepts[0]}>
                            {selectedDepts.length === departments.length ? (
                              'All'
                            ) : (
                              <>
                                <TruncateFilterFieldView
                                  textContent={departments.find((item) => item._id === selectedDepts[0])?.user_department}
                                />
                                {selectedDepts.length > 1 && selectedDepts.length !== departments.length && (
                                  <div className="selected-option-count">
                                    {selectedDepts.length - 1}+
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {isDeptModalDropdownOpen && (
                        <div className="dropdown-list1">
                          <input
                            type="text"
                            placeholder="Search departments"
                            value={departmentDropdownSearchTerm}
                            onChange={onDepartmentDropdownSearchChange}
                            className="search-bar"
                          />
                          {filteredDropdownDepartments.length > 0 && (
                            <>
                              {departmentDropdownSearchTerm === "" && (
                                <label className="checkbox-label1" key="selectAll">
                                  <input
                                    type="checkbox"
                                    checked={selectAllDept}
                                    onChange={onSelectAllDeptChange}
                                  />
                                  All Departments
                                </label>
                              )}
                              {filteredDropdownDepartments
                                .slice()
                                .sort((a, b) =>
                                  a.user_department?.toLowerCase().localeCompare(b.user_department?.toLowerCase())
                                )
                                .map((dept) => (
                                  <label className="checkbox-label1" key={dept._id}>
                                    <input
                                      type="checkbox"
                                      checked={selectedDepts.includes(dept._id)}
                                      onChange={() => onCheckboxDeptChange(dept._id)}
                                    />
                                    {dept.user_department}
                                  </label>
                                ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expense Category Filter in Modal */}
                <div className="filter-inner payment-status">
                  <label htmlFor="paymentStatus">Expense Category</label>
                  <div className="custom-dropdown1" ref={expCategoryModalDropdownRef}>
                    <div className="drop-down-inner">
                      <div className="selected-options1" onClick={onToggleExpCategoryModalDropdown}>
                        {selectAllExpCategory ? (
                          <div className="selected-option1">All</div>
                        ) : selectedExpCategories.length === 0 ? (
                          <div className="placeholder">Select</div>
                        ) : (
                          <div className="selected-option1" key={selectedExpCategories[0]}>
                            {selectedExpCategories.length === expenseCategory.length ? (
                              'All'
                            ) : (
                              <>
                                <TruncateFilterFieldView
                                  textContent={expenseCategory.find((item) => item._id === selectedExpCategories[0])?.exp_category_name}
                                />
                                {selectedExpCategories.length > 1 && selectedExpCategories.length !== expenseCategory.length && (
                                  <div className="selected-option-count">
                                    {selectedExpCategories.length - 1}+
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {isExpCategoryModalDropdownOpen && (
                        <div className="dropdown-list1">
                          <input
                            type="text"
                            placeholder="Search categories"
                            value={categoryDropdownSearchTerm}
                            onChange={onCategoryDropdownSearchChange}
                            className="search-bar"
                          />
                          {filteredDropdownExpenseCategory.length > 0 && (
                            <>
                              {categoryDropdownSearchTerm === "" && (
                                <label className="checkbox-label1" key="selectAll">
                                  <input
                                    type="checkbox"
                                    checked={selectAllExpCategory}
                                    onChange={onSelectAllExpCategoryChange}
                                  />
                                  All Expense Category
                                </label>
                              )}
                              {filteredDropdownExpenseCategory
                                .slice()
                                .sort((a, b) =>
                                  a.exp_category_name?.toLowerCase().localeCompare(b.exp_category_name?.toLowerCase())
                                )
                                .map((expCat) => (
                                  <label className="checkbox-label1" key={expCat._id}>
                                    <input
                                      type="checkbox"
                                      checked={selectedExpCategories.includes(expCat._id)}
                                      onChange={() => onCheckboxExpCategoryChange(expCat._id)}
                                    />
                                    {expCat.exp_category_name}
                                  </label>
                                ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expense Currency Filter in Modal */}
                <div className="filter-inner payment-status">
                  <label htmlFor="paymentStatus">Expense Currency</label>
                  <div className="custom-dropdown1" ref={expCurrencyFilterModalDropdownRef}>
                    <div className="drop-down-inner">
                      <div className="selected-options1" onClick={onToggleExpCurrencyFilterModalDropdown}>
                        {selectAllExpCurrencyFilter ? (
                          <div className="selected-option1">All</div>
                        ) : selectedExpCurrenciesFilter.length === 0 ? (
                          <div className="placeholder">Select</div>
                        ) : (
                          <div className="selected-option1" key={selectedExpCurrenciesFilter[0]}>
                            {selectedExpCurrenciesFilter.length === expCurrencyList.length ? (
                              'All'
                            ) : (
                              <>
                                <TruncateFilterFieldView
                                  textContent={expCurrencyList.find((item) => item.currencyCode === selectedExpCurrenciesFilter[0])?.currencyName}
                                />
                                {selectedExpCurrenciesFilter.length > 1 && selectedExpCurrenciesFilter.length !== expCurrencyList.length && (
                                  <div className="selected-option-count">
                                    {selectedExpCurrenciesFilter.length - 1}+
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {isExpCurrencyFilterModalDropdownOpen && (
                        <div className="dropdown-list1">
                          <input
                            type="text"
                            placeholder="Search currency"
                            value={expCurrencyDropdownSearchTerm}
                            onChange={onExpCurrencyDropdownSearchChange}
                            className="search-bar"
                          />
                          {filteredDropdownExpCurrencyList.length > 0 && (
                            <>
                              {expCurrencyDropdownSearchTerm === "" && (
                                <label className="checkbox-label1" key="selectAll">
                                  <input
                                    type="checkbox"
                                    checked={selectAllExpCurrencyFilter}
                                    onChange={onSelectAllExpCurrencyFilterChange}
                                  />
                                  All
                                </label>
                              )}
                              {filteredDropdownExpCurrencyList
                                .slice()
                                .sort((a, b) =>
                                  a.currencyName?.toLowerCase().localeCompare(b.currencyName?.toLowerCase())
                                )
                                .map((expCur) => (
                                  <label className="checkbox-label1" key={expCur.currencyCode}>
                                    <input
                                      type="checkbox"
                                      checked={selectedExpCurrenciesFilter.includes(expCur.currencyCode)}
                                      onChange={() => onCheckboxExpCurrencyFilterChange(expCur.currencyCode)}
                                    />
                                    {expCur.currencyCode && `${expCur.currencyCode} (${expCur.currencyName})`}
                                  </label>
                                ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expense Status Filter in Modal */}
                <div className="filter-inner payment-status">
                  <label htmlFor="expenseStatus">Expense Status</label>
                  <div className="custom-dropdown1" ref={expStatusFilterDropdownRef}>
                    <div className="drop-down-inner">
                      <div className="selected-options1" onClick={onToggleExpStatusFilterDropdown}>
                        {selectAllExpStatusFilter ? (
                          <div className="selected-option1">All</div>
                        ) : selectedExpStatusFilter.length === 0 ? (
                          <div className="placeholder">Select</div>
                        ) : (
                          <div className="selected-option1" key={selectedExpStatusFilter[0]}>
                            {selectedExpStatusFilter.length === EXPENSE_STATUS_LIST.length ? (
                              'All'
                            ) : (
                              <>
                                <TruncateFilterFieldView
                                  textContent={EXPENSE_STATUS_LIST.find((item) => item.value === selectedExpStatusFilter[0])?.label}
                                />
                                {selectedExpStatusFilter.length > 1 && selectedExpStatusFilter.length !== EXPENSE_STATUS_LIST.length && (
                                  <div className="selected-option-count">
                                    {selectedExpStatusFilter.length - 1}+
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      {isExpStatusFilterDropdownOpen && (
                        <div className="dropdown-list1">
                          <input
                            type="text"
                            placeholder="Search status"
                            value={expStatusDropdownSearchTerm}
                            onChange={onExpStatusDropdownSearchChange}
                            className="search-bar"
                          />
                          {filteredExpStatusList.length > 0 && (
                            <>
                              {expStatusDropdownSearchTerm === "" && (
                                <label className="checkbox-label1" key="selectAllStatus">
                                  <input
                                    type="checkbox"
                                    checked={selectAllExpStatusFilter}
                                    onChange={onSelectAllExpStatusFilterChange}
                                  />
                                  All
                                </label>
                              )}
                              {filteredExpStatusList
                                .slice()
                                .sort((a, b) => a.label?.toLowerCase().localeCompare(b.label?.toLowerCase()))
                                .map((status) => (
                                  <label className="checkbox-label1" key={status.value}>
                                    <input
                                      type="checkbox"
                                      checked={selectedExpStatusFilter.includes(status.value)}
                                      onChange={() => onCheckboxExpStatusFilterChange(status.value)}
                                    />
                                    {status.label}
                                  </label>
                                ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Date Range Filter in Modal */}
                <div className="filter-inner due-date">
                  <label htmlFor="dueDate" className="calendar-icon">
                    Date Range
                  </label>
                  <div className="date-picker-outer date-picker-outer-all" ref={calendarModalRef} style={{ position: 'relative' }}>
                    <div className="custom-picker-icon custom-picker-icon-all">
                      <svg
                        width="1em"
                        height="1em"
                        viewBox="0 0 14 14"
                        fill="currentColor"
                        aria-hidden="true"
                        focusable="false"
                        className="rs-picker-toggle-caret rs-icon"
                        aria-label="calendar"
                        data-category="legacy"
                      >
                        <path d="M1 4v8.5a.5.5 0 00.5.5H8c0-.128.049-.256.146-.354.555-.555.854-1.6.854-3.146a.5.5 0 01.621-.485l.119.03A2.623 2.623 0 0012.999 6.5V4h-12zm3-2h6V0h1v2h1.5A1.5 1.5 0 0114 3.5v3a3.623 3.623 0 01-4.015 3.603c-.064 1.245-.335 2.212-.831 2.898H12.5a.5.5 0 00.5-.5v-2a.5.5 0 011 0v2a1.5 1.5 0 01-1.5 1.5h-11a1.5 1.5 0 01-1.5-1.5v-9a1.5 1.5 0 011.5-1.5H3v-2h1v2zm5 4V5h1v1h2v1h-2v1H9V7H7v2h1v1H7v2H6v-2H4v2H3v-2H1V9h2V7H1V6h2V5h1v1h2V5h1v1h2zM6 9V7H4v2h2z"></path>
                      </svg>
                    </div>
                    <div className="custoom-icon-calender custoom-icon-calender-all">
                      <div className="custoom-icon-calender custoom-icon-calender-all custom-date-range">
                        <input
                          type="text"
                          value={
                            dateRange
                              ? `${new Date(dateRange[0]).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                })} to ${new Date(dateRange[1]).toLocaleDateString("en-GB", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                })}`
                              : ''
                          }
                          id="date-range"
                          name="date-range"
                          placeholder="All Time"
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleModalCalendar();
                          }}
                          autoComplete="off"
                          readOnly
                        />
                      </div>
                        {showModalCalendar && ReactDOM.createPortal(
                          <div
                            ref={calendarModalPortalRef}
                            style={{
                              position: 'fixed',
                              inset: 0,
                              zIndex: 10000,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: 'rgba(0,0,0,0.5)',
                            }}
                            onClick={(e) => { if (e.target === e.currentTarget) onCancelModalCalendar(); }}
                          >
                            <div className="date-range-new date-range-portal">
                              <Calendar
                                onCancel={onCancelModalCalendar}
                                onChange={(range) => {
                                  if (range && range.startDate && range.endDate) {
                                    onDateChange({
                                      startDate: new Date(range.startDate),
                                      endDate: new Date(range.endDate),
                                    });
                                  }
                                }}
                                initialRange={memoizedInitialRange}
                              />
                            </div>
                          </div>,
                          document.body
                        )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Confirmation Modal */}
              <Modal
                className="delete-modal"
                isOpen={showConfirmationModal}
                onRequestClose={onCancel}
                contentLabel="Confirm Delete"
                style={MODAL_STYLES.CONFIRMATION_MODAL}
              >
                <div className="delete-modal-content">
                  <h2>Apply or Discard changes?</h2>
                  <p>Do you want to Apply or Discard filter changes?</p>
                  <div className="delete-modal-buttons">
                    <button className="delete" onClick={onApplyConfirm}>
                      Apply Changes
                    </button>
                    <button onClick={onClearFilters}>
                      Discard Changes
                    </button>
                  </div>
                </div>
              </Modal>

              {/* Filter Buttons */}
              <div className="filter-buttons">
                <button className="clear-all" onClick={onClearFilters}>
                  Clear All
                </button>
                <button className="apply-filter" onClick={onApplyFilters}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="17" viewBox="0 0 13 17" fill="none">
                    <path
                      d="M11.7363 8.81108C11.4276 9.81451 10.4925 10.5444 9.38889 10.5444C8.28471 10.5444 7.35016 9.8141 7.04147 8.81108L0.722221 8.81108C0.323349 8.81108 -1.07845e-06 8.48773 -1.06101e-06 8.08886C-1.04357e-06 7.68998 0.323349 7.36663 0.722221 7.36663L7.04144 7.36663C7.35022 6.3632 8.28529 5.6333 9.38889 5.6333C10.4931 5.6333 11.4276 6.36362 11.7363 7.36663L12.2778 7.36663C12.6767 7.36663 13 7.68998 13 8.08886C13 8.48773 12.6766 8.81108 12.2778 8.81108L11.7363 8.81108ZM9.38889 7.07775C8.83162 7.07775 8.37778 7.53123 8.37778 8.08886C8.37778 8.64613 8.83127 9.09997 9.38889 9.09997C9.94616 9.09997 10.4 8.64648 10.4 8.08886C10.4 7.53158 9.94651 7.07775 9.38889 7.07775Z"
                      fill="white"
                    />
                    <path
                      d="M5.38078 3.17778C5.072 4.18121 4.13693 4.91111 3.03333 4.91111C1.92882 4.91111 0.994035 4.18035 0.68563 3.17687C0.303759 3.15781 -1.07791e-06 2.84216 -1.06101e-06 2.45556C-1.04411e-06 2.06896 0.303748 1.75331 0.685606 1.73425C0.994098 0.730343 1.9294 -4.83911e-07 3.03333 -4.35657e-07C4.13752 -3.87392e-07 5.07206 0.730315 5.38076 1.73333L12.2778 1.73333C12.6767 1.73333 13 2.05668 13 2.45556C13 2.85443 12.6766 3.17778 12.2778 3.17778L5.38078 3.17778ZM3.03333 1.44444C2.47606 1.44444 2.02222 1.89793 2.02222 2.45556C2.02222 3.01283 2.47571 3.46667 3.03333 3.46667C3.5906 3.46667 4.04444 3.01318 4.04444 2.45556C4.04444 1.89828 3.59096 1.44444 3.03333 1.44444Z"
                      fill="white"
                    />
                    <path
                      d="M7.403 14.4444C7.09422 15.4478 6.15916 16.1777 5.05556 16.1777C3.95137 16.1777 3.01682 15.4474 2.70813 14.4444L0.722221 14.4444C0.323349 14.4444 -1.07845e-06 14.121 -1.06101e-06 13.7222C-1.04357e-06 13.3233 0.323349 12.9999 0.722221 12.9999L2.70811 12.9999C3.01689 11.9965 3.95195 11.2666 5.05556 11.2666C6.15974 11.2666 7.09429 11.9969 7.40298 12.9999L12.2778 12.9999C12.6767 12.9999 13 13.3233 13 13.7222C13 14.121 12.6766 14.4444 12.2778 14.4444L7.403 14.4444ZM5.05556 12.711C4.49828 12.711 4.04444 13.1645 4.04444 13.7222C4.04444 14.2794 4.49793 14.7333 5.05556 14.7333C5.61283 14.7333 6.06667 14.2798 6.06667 13.7222C6.06667 13.1649 5.61318 12.711 5.05556 12.711Z"
                      fill="white"
                    />
                  </svg>
                  Apply Filters
                </button>
              </div>
            </div>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default ExpenseFilters;
